import { t, Trans } from '@lingui/macro'
import { Col, Form, Row } from 'antd'
import DividerElement from 'components/atoms/divider'
import FormDatePicker from 'components/molecules/form-item/FormDatePicker'
import FormInput from 'components/molecules/form-item/FormInput'
import FormSelect from 'components/molecules/form-item/FormSelect'
import { selectText } from 'data/messages/controls'
import { nameInputRules, required } from 'helpers/Validators.helpers'
import { formatInputDate } from 'helpers/Dates.helpers'
import trans from 'helpers/i18n.helpers'
import { FieldData } from 'models/components'
import { SettingsFields } from 'models/project/projectDetails'
import moment, { Moment } from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { setExpectedCompletionDate, setGeneralSettingsFields } from 'redux/project/project-config/general'
import {
  selectCanEnd,
  selectExpectedCompletionDate,
  selectGeneralSettingsFields,
  selectInitialSettingsFetched,
} from 'redux/project/project-config/general/ProjectGeneralConfig.selectors'
import { selectProjectDetails } from 'redux/project/project-details/ProjectDetails.selectors'
import useFormValidity from 'hooks/useFormValidity'
import {
  PROJECT_CONTRACTING_AUTHORITY_LENGTH,
  PROJECT_LOCATION_LENGTH,
  PROJECT_SECTOR_LENGTH,
} from 'data/model_restrictions'

const activationOptions = [
  { label: <Trans id="project_config.general.form.activation.active">Active</Trans>, value: 0 },
  { label: <Trans id="project_config.general.form.activation.finished">Finished</Trans>, value: 1 },
]

const ProjectGeneralConfigForm: React.FC = () => {
  const initialSettingsFetched = useSelector(selectInitialSettingsFetched)
  const { form, validate, getFormItemErrors } = useFormValidity(initialSettingsFetched)
  const dispatch = useDispatch()
  const onExpectedCompletionDateChange = (val: Moment | null, dateStr: string) =>
    dispatch(setExpectedCompletionDate(dateStr))

  const onChange = async (changedFields: FieldData<string | number>[], allFields: FieldData<string | number>[]) => {
    dispatch(setGeneralSettingsFields(allFields as SettingsFields))
    validate()
  }
  const generalSettingsFields = useSelector(selectGeneralSettingsFields)
  const projectDetails = useSelector(selectProjectDetails)
  const expectedCompletionDate = useSelector(selectExpectedCompletionDate)
  const canEnd = useSelector(selectCanEnd)

  return (
    <Form layout="vertical" form={form} fields={generalSettingsFields} onFieldsChange={onChange}>
      <Row gutter={32}>
        <Col span={10}>
          <FormInput
            name="name"
            label={t({ id: 'project_config.general.form.label.name', message: 'Name' })}
            placeholder={t({ id: 'project_config.general.form.set_name_placeholder', message: 'Enter name' })}
            rules={nameInputRules()}
            getErrors={getFormItemErrors}
          />
          <FormInput
            label={t({ id: 'project_config.general.form.label.creation_date', message: 'Creation date' })}
            placeholder={projectDetails && formatInputDate(projectDetails.creationDate)}
            disabled
          />
        </Col>
        <Col span={10}>
          <FormInput
            label={t({ id: 'project_config.general.form.label.creator', message: 'Creator' })}
            disabled
            placeholder={projectDetails?.creator.name}
          />
          <FormSelect
            disabled={!canEnd}
            options={activationOptions}
            name="isEnded"
            label={t({ id: 'project_config.general.form.label.status', message: 'Project status' })}
            placeholder={trans(selectText)}
          />
        </Col>
      </Row>
      <DividerElement size="m" />
      <Row gutter={32}>
        <Col span={10}>
          <FormDatePicker
            onChange={onExpectedCompletionDateChange}
            value={moment(expectedCompletionDate || undefined)}
            label={t({ id: 'project_config.general.form.label.completion_date', message: 'Expected completion date' })}
            rules={[required()]}
          />
          <FormInput
            name="sector"
            label={t({ id: 'project_config.general.form.label.sector', message: 'Sector' })}
            placeholder={t({ id: 'project_config.general.form.set_sector_placeholder', message: 'Enter sector' })}
            rules={nameInputRules(PROJECT_SECTOR_LENGTH)}
            getErrors={getFormItemErrors}
          />
          <FormInput
            name="location"
            label={t({ id: 'project_config.general.form.label.location', message: 'Location' })}
            placeholder={t({ id: 'project_config.general.form.set_location_placeholder', message: 'Enter location' })}
            rules={nameInputRules(PROJECT_LOCATION_LENGTH)}
            getErrors={getFormItemErrors}
          />
          <FormInput
            name="contractingAuthority"
            label={t({
              id: 'project_config.general.form.label.contracting_authority',
              message: 'Contracting authority',
            })}
            placeholder={t({
              id: 'project_config.general.form.set_authority_placeholder',
              message: 'Enter authority name',
            })}
            rules={nameInputRules(PROJECT_CONTRACTING_AUTHORITY_LENGTH)}
            getErrors={getFormItemErrors}
          />
        </Col>
      </Row>
    </Form>
  )
}

export default ProjectGeneralConfigForm
